import { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Stack,
    Paper,
    Button,
    Typography,
    Grid,
    Dialog,
    DialogTitle,
    DialogContent,
    Skeleton,
    useTheme,
    useMediaQuery,
    Box,
} from '@mui/material';
import IconButton from '@mui/material/IconButton';
import { AutoGraphRounded, CalendarMonthRounded, Close, RemoveRedEyeRounded } from '@mui/icons-material';
import Markdown from 'react-markdown';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';

import { PostType } from '@/graphql/generated/types.generated';
import { t } from 'i18next';
import { numberFormat } from '@/utils/number-format';
import { ClockIcon } from '@mui/x-date-pickers/icons';
import { StepInfo } from './StepInfo';
import { StringRotation } from '../StringRotation';
import { ActionResponseStatusAi } from '@/ai/enum/action-response-status-ai';
import markdownToPlainText from '@/utils/markdown-to-plain-text';
import { posthog } from 'posthog-js';

export type BriefingPreviewProps = {
    postTypeString: PostType;
    postFormatString: string;
    uuid: string;
    topicJustification: string;
    postTypeJustification: string;
    dateJustification: string;
    postType: string;
    postFormat: string;
    briefing?: string;
    dateTime: Date;
    topic?: string;
};

export type ForecastData = {
    overview?: string;
    engagementRate?: number;
    avgLikes?: number;
    avgComments?: number;
    weeklyReach?: number;
    weeklyImpressions?: number;
    profileVisits?: number;
    newFollowers?: number;
    storiesViews?: number;
    timeframe?: {
        from: number;
        to: number;
    };
};

export type ContentPlanProps = {
    dates: BriefingPreviewProps[];
};

export const ConfirmFinalPlan = ({ onEnd }: { onEnd: (option: string) => void }) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const container = useRef<HTMLDivElement>(null);

    useEffect(() => {
        container.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }, [container]);

    return (
        <Box ref={container} mt={3}>
            <Box
                onClick={() => onEnd('import')}
                sx={{
                    p: { xs: 3, sm: 4 },
                    borderRadius: 4,
                    background: `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.secondary.main} 100%)`,
                    color: theme.palette.primary.contrastText,
                    textAlign: 'center',
                    cursor: 'pointer',
                    transition: 'transform 0.15s ease, box-shadow 0.15s ease',
                    '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: theme.shadows[8],
                    },
                }}
            >
                <Stack alignItems="center" spacing={2}>
                    <Stack
                        alignItems="center"
                        justifyContent="center"
                        sx={{
                            width: 64,
                            height: 64,
                            borderRadius: '50%',
                            bgcolor: 'rgba(255, 255, 255, 0.18)',
                        }}
                    >
                        <AutoAwesomeRoundedIcon fontSize="large" />
                    </Stack>
                    <Typography variant="h5" fontWeight={700}>
                        {t('onboarding.chat.confirm-plan.unlock-title')}
                    </Typography>
                    <Typography variant="body1" sx={{ maxWidth: 520, opacity: 0.95 }}>
                        {t('onboarding.chat.confirm-plan.unlock-desc')}
                    </Typography>
                    <Button
                        variant="contained"
                        size="large"
                        endIcon={<ArrowForwardRoundedIcon />}
                        onClick={(e) => {
                            e.stopPropagation();
                            onEnd('import');
                        }}
                        sx={{
                            mt: 1,
                            bgcolor: 'common.white',
                            color: 'primary.main',
                            fontWeight: 700,
                            '&:hover': { bgcolor: theme.palette.grey[100] },
                        }}
                    >
                        {t('onboarding.chat.confirm-plan.unlock-action')}
                    </Button>
                </Stack>
            </Box>
        </Box>
    );
};

const BriefingPreview = (props: BriefingPreviewProps) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [open, setOpen] = useState(false);
    const item = props;

    const POST_TYPE_COLORS: Record<string, string> = {
        STATUS: theme.palette.info.light,
        MEDIA: theme.palette.primary.light,
        STORY: theme.palette.warning.light,
        LINK: theme.palette.secondary.light,
        REEL: theme.palette.success.light,
    };

    const typeColor = POST_TYPE_COLORS[item.postTypeString] || theme.palette.grey[200];

    useEffect(() => {
        if (item && open) {
            posthog.capture('onboarding.briefing-preview', {
                topic: item.topic,
                postType: item.postTypeString,
                postFormat: item.postFormatString,
                dateTime: item.dateTime,
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [item.topic, open]);

    return (
        <Grid key={item.uuid} size={{ xs: 12, sm: 6 }}>
            <Paper
                elevation={1}
                sx={{
                    p: 2,
                    cursor: item.briefing ? 'pointer' : 'default',
                    '&:hover': item.briefing
                        ? {
                              background: theme.palette.grey[50],
                          }
                        : {},
                }}
                onClick={item.briefing ? () => setOpen(true) : undefined}
            >
                <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                            {item.topic}
                        </Typography>
                    </Stack>
                    <Typography
                        variant="caption"
                        sx={{
                            px: 1.5,
                            py: 0.5,
                            borderRadius: 2,
                            bgcolor: typeColor,
                            color: theme.palette.getContrastText(typeColor),
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {item.postTypeString} {item.postFormatString}
                    </Typography>
                </Stack>
                <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                        mt: 1,
                        mb: 1,
                        overflow: 'hidden',
                        display: '-webkit-box',
                        WebkitLineClamp: 1,
                        WebkitBoxOrient: 'vertical',
                    }}
                >
                    {item.briefing ? (
                        markdownToPlainText(item.briefing)
                            .replace(/(-|\*)/g, '')
                            .split('\n')
                            .filter((l) => l.trim().length > 0)[4] // get the 5th line
                    ) : (
                        <Skeleton width="100%" />
                    )}
                </Typography>
                <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
                    <Stack direction="row" alignItems="center" spacing={1} color="text.secondary">
                        <ClockIcon sx={{ fontSize: 18 }} />
                        <Typography variant="caption">
                            {new Date(item.dateTime).toLocaleTimeString([], {
                                day: '2-digit',
                                month: 'short',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                            })}
                        </Typography>
                    </Stack>
                    {item.briefing ? (
                        <Button startIcon={<RemoveRedEyeRounded />}>
                            <Typography>{t('preview')}</Typography>
                        </Button>
                    ) : (
                        <Skeleton width="70px" height="30px" />
                    )}
                </Stack>
            </Paper>
            {props.briefing && (
                <Dialog
                    open={open}
                    onClose={() => setOpen(false)}
                    maxWidth="md"
                    fullWidth
                    fullScreen={isMobile}
                >
                    <DialogTitle>
                        <Stack direction="row" alignItems="center" spacing={2}>
                            <Typography
                                variant="caption"
                                sx={{
                                    px: 1.5,
                                    py: 0.5,
                                    borderRadius: 2,
                                    bgcolor: typeColor,
                                    color: theme.palette.getContrastText(typeColor),
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {item.postTypeString} {item.postFormatString}
                            </Typography>
                            <Typography variant="h6" width={1} display="flex" alignItems="center" gap={1}>
                                <ClockIcon fontSize="small" />
                                {new Date(item.dateTime).toLocaleDateString([], {
                                    day: '2-digit',
                                    month: 'short',
                                    year: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit',
                                })}
                            </Typography>
                            <IconButton
                                aria-label="Close"
                                onClick={() => setOpen(false)}
                                sx={{ float: 'right' }}
                            >
                                <Close />
                            </IconButton>
                        </Stack>
                    </DialogTitle>
                    <DialogContent dividers>
                        <Markdown
                            components={{
                                a: ({ children, href, ...props }) => (
                                    <a href={href} target="_blank" rel="noopener noreferrer" {...props}>
                                        {children}
                                    </a>
                                ),
                            }}
                        >
                            {props.briefing}
                        </Markdown>
                    </DialogContent>
                </Dialog>
            )}
        </Grid>
    );
};

export const ShowBriefings = ({ data }: { data: BriefingPreviewProps[] }) => {
    return (
        <Stack direction="column" spacing={1} mt={2}>
            <Typography variant="h5" textAlign="center">
                <CalendarMonthRounded /> {t('onboarding.chat.suggested-plan')}
            </Typography>
            <Grid container columns={{ xs: 12 }} spacing={2} p={1}>
                {[...data]
                    .sort((a, b) => new Date(a.dateTime).getTime() - new Date(b.dateTime).getTime())
                    .map((item) => (
                        <BriefingPreview key={item.uuid} {...item} />
                    ))}
            </Grid>
        </Stack>
    );
};

export const ResumeForecast = ({ finalData }: { finalData: ForecastData | null }) => {
    const theme = useTheme();

    const renderCard = (title: string, value: number | undefined, suffix = '') => {
        return (
            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={title}>
                <Box
                    sx={{
                        background: theme.palette.action.selected,
                        color: theme.palette.primary.contrastText,
                        p: 2,
                        borderRadius: 2,
                        textAlign: 'center',
                    }}
                >
                    {value !== undefined ? (
                        <>
                            <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
                                {value > 0 ? '+' : value < 0 ? '-' : ''}
                                {numberFormat(value)}
                                {suffix}
                            </Typography>
                            <Typography variant="body2">{title}</Typography>
                        </>
                    ) : (
                        <Stack spacing={1} alignItems="center">
                            <Skeleton
                                variant="rectangular"
                                width="30%"
                                height={28}
                                sx={{ bgcolor: theme.palette.primary.contrastText }}
                            />
                            <Skeleton
                                variant="rectangular"
                                width="60%"
                                height={16}
                                sx={{ bgcolor: theme.palette.primary.contrastText }}
                            />
                        </Stack>
                    )}
                </Box>
            </Grid>
        );
    };

    return (
        <Paper
            sx={{
                p: 3,
                borderRadius: 4,
                background: `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.secondary.main} 100%)`,
                color: theme.palette.primary.contrastText,
            }}
        >
            <Stack spacing={2}>
                <Typography
                    variant="h5"
                    sx={{
                        gap: 1,
                        color: theme.palette.primary.contrastText,
                    }}
                >
                    <AutoGraphRounded sx={{ color: theme.palette.primary.contrastText }} />{' '}
                    {t('onboarding.chat.content-plan.forecast.title')}
                </Typography>
                {/* Overview */}
                <Box
                    sx={{
                        background: theme.palette.action.selected,
                        color: theme.palette.primary.contrastText,
                        p: 2,
                        borderRadius: 2,
                    }}
                >
                    <Typography variant="subtitle1">
                        {t('onboarding.chat.content-plan.forecast.overview')}
                    </Typography>
                    {finalData?.overview ? (
                        <Typography
                            variant="body2"
                            sx={{ color: theme.palette.primary.contrastText }}
                            component="span"
                        >
                            <Markdown
                                components={{
                                    a: ({ children, href, ...props }) => (
                                        <a href={href} target="_blank" rel="noopener noreferrer" {...props}>
                                            {children}
                                        </a>
                                    ),
                                }}
                            >
                                {finalData.overview}
                            </Markdown>
                        </Typography>
                    ) : (
                        <Stack spacing={1}>
                            <Skeleton
                                variant="rectangular"
                                width="100%"
                                height={20}
                                sx={{ bgcolor: theme.palette.primary.contrastText }}
                            />
                            <Skeleton
                                variant="rectangular"
                                width="80%"
                                height={20}
                                sx={{ bgcolor: theme.palette.primary.contrastText }}
                            />
                        </Stack>
                    )}
                </Box>
                <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap" justifyContent="flex-start">
                    <Grid container columns={{ xs: 12 }} spacing={2} width={1}>
                        {renderCard(
                            t('onboarding.chat.content-plan.forecast.engagement-rate'),
                            finalData?.engagementRate,
                            '%',
                        )}
                        {renderCard(
                            t('onboarding.chat.content-plan.forecast.avg-likes'),
                            finalData?.avgLikes,
                        )}
                        {renderCard(
                            t('onboarding.chat.content-plan.forecast.avg-comments'),
                            finalData?.avgComments,
                        )}
                        {renderCard(
                            t('onboarding.chat.content-plan.forecast.weekly-reach'),
                            finalData?.weeklyReach,
                        )}
                        {renderCard(
                            t('onboarding.chat.content-plan.forecast.weekly-impressions'),
                            finalData?.weeklyImpressions,
                        )}
                        {renderCard(
                            t('onboarding.chat.content-plan.forecast.profile-visits'),
                            finalData?.profileVisits,
                        )}
                        {renderCard(
                            t('onboarding.chat.content-plan.forecast.new-followers'),
                            finalData?.newFollowers,
                        )}
                        {renderCard(
                            t('onboarding.chat.content-plan.forecast.stories-views'),
                            finalData?.storiesViews,
                        )}
                    </Grid>
                </Stack>
            </Stack>
        </Paper>
    );
};

export const StepInfoPlan = ({ status }: { status: `${ActionResponseStatusAi}` }) => {
    const { t } = useTranslation();
    return (
        <StepInfo
            status={status}
            currentStep={4}
            totalSteps={5}
            titleComp={t('onboarding.chat.generate-content-plan')}
            processingComp={
                <StringRotation
                    messages={[
                        ...[
                            t('onboarding.chat.generating-plan'),
                            t('onboarding.chat.analyzing-profile-data'),
                            t('onboarding.chat.analyzing-competitors-data'),
                            t('onboarding.chat.finding-best-dates'),
                            t('onboarding.chat.defining-content-ideas'),
                        ],
                        ...[
                            t('onboarding.chat.justifying-briefings'),
                            t('onboarding.chat.forecasting-performance'),
                        ],
                        ...[
                            t('onboarding.chat.generating-briefings'),
                            t('onboarding.chat.making-final-adjustments'),
                        ],
                    ]}
                    intervalMs={5000}
                />
            }
            finishedComp={<>{t('onboarding.chat.final-plan-title')}</>}
        />
    );
};

export const StepInfoForecast = ({ status }: { status: `${ActionResponseStatusAi}` }) => {
    const { t } = useTranslation();
    return (
        <StepInfo
            status={status}
            currentStep={5}
            totalSteps={5}
            titleComp={t('onboarding.chat.forecast-title')}
            processingComp={
                <StringRotation
                    messages={[
                        t('onboarding.chat.forecasting-performance'),
                        t('onboarding.chat.analyzing-profile-data'),
                        t('onboarding.chat.making-final-adjustments'),
                    ]}
                    intervalMs={4000}
                />
            }
            finishedComp={<>{t('onboarding.chat.content-plan.forecast.title')}</>}
        />
    );
};
